import axios from "axios";
import { FormEvent, useCallback, useEffect, useRef, useState } from "react";

import {
  makeStyles,
  TextField,
  Dialog,
  DialogContent,
  Button,
  DialogTitle,
  DialogActions,
  CircularProgress
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";

import getTenant from "src/util/getTenant";

import getAdminApiBaseUrl from "../../util/getAdminApiBaseUrl";
import { useNonStaffAuth0 } from "../AuthProvider";
import { useCaptcha } from "../CaptchaProvider";
import LoadingIndicator from "../LoadingIndicator";
import LogOutLink from "../LogOutLink";
import { useLoginInfoRefetch } from "../LoggedInAdminProvider";
import SubmitButton from "../form/SubmitButton";
import Layout from "./Layout";

type Status = { status: "NO_TOKEN" | "TOKEN_SENT"; email: string };

type RequestNewError = "EXCEEDED_TOKEN_LIMIT" | "UNKNOWN";

type VerifyError =
  | "MISSING_TOKEN"
  | "NO_TOKEN"
  | "TOKEN_EXPIRED"
  | "TOKEN_GUESS_LIMIT_EXCEEDED"
  | "INVALID_TOKEN"
  | "UNKNOWN";

function getVerifyErrorMessage(error: VerifyError) {
  switch (error) {
    case "MISSING_TOKEN":
      return "No code was provided.";
    case "NO_TOKEN":
      return "You have not yet requested a code.";
    case "TOKEN_EXPIRED":
      return "That code is expired. Please request a new one.";
    case "TOKEN_GUESS_LIMIT_EXCEEDED":
      return "You've submitted too many guesses. Please request a new code.";
    case "INVALID_TOKEN":
      return "That code is incorrect.";
    case "UNKNOWN":
      return "An error occurred while checking your code. Please try again.";
  }
}

function cleanCodeInput(input: string): string {
  return input.replace(/[^0-9]/g, "").substr(0, 6);
}

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3)
  },
  errorMessageSection: {
    height: "60px"
  },
  inlineForm: {
    display: "flex",
    flexDirection: "row",
    "& .MuiInputBase-root": {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0
    },
    "& .MuiButton-root": {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0
    }
  },
  verifyInput: {
    flex: 1
  },
  verifyButton: {
    width: "150px"
  }
}));

function RequestNew({
  requestNewLoading,
  requestNewError,
  requestNewSucceeded,
  handleRequestNewCode,
  handleDismissDialog
}: {
  requestNewLoading: boolean;
  requestNewError: RequestNewError | null;
  requestNewSucceeded: boolean;
  handleRequestNewCode: () => void;
  handleDismissDialog: () => void;
}) {
  if (requestNewLoading) {
    return <CircularProgress size={16} />;
  }

  if (requestNewSucceeded) {
    return <span>New code sent.</span>;
  }

  return (
    <>
      <span>
        Need a new code?{" "}
        <a href="#" onClick={handleRequestNewCode}>
          Request a new one
        </a>
        .
      </span>
      <Dialog
        open={!!requestNewError}
        onClose={handleDismissDialog}
        aria-labelledby="alert-dialog-title"
      >
        <DialogTitle id="alert-dialog-title">
          Could not request new token
        </DialogTitle>
        <DialogContent>
          {requestNewError === "EXCEEDED_TOKEN_LIMIT" ? (
            <p>
              We have already sent three verification codes, and cannot send any
              more. Please{" "}
              <a
                href="https://scaletowin.freshdesk.com/support/tickets/new"
                target="_blank"
                rel="noopener noreferrer"
              >
                contact support for help
              </a>
              .
            </p>
          ) : (
            <p>
              An error occurred while requesting a new code. Please try again.
            </p>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDismissDialog}
            variant="contained"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function EmailVerification(): JSX.Element {
  const classes = useStyles();
  const refetchLoginInfo = useLoginInfoRefetch();

  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status | null>(null);

  const [requestNewLoading, setRequestNewLoading] = useState(false);
  const [requestNewError, setRequestNewError] =
    useState<RequestNewError | null>(null);
  const [requestNewSucceeded, setRequestNewSucceeded] = useState(false);

  const [verifyCode, setVerifyCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<VerifyError | null>(null);
  const inputRef = useRef<HTMLFormElement>();

  const auth0 = useNonStaffAuth0();
  const captcha = useCaptcha();

  const getAxiosClient = useCallback(async () => {
    const token = await auth0.getAccessTokenSilently();

    const client = axios.create({
      baseURL: `${await getAdminApiBaseUrl()}/admin/email_verification/`,
      headers: {
        Authorization: token ? `Bearer ${token}` : "",
        "STW-Dialer-Tenant": getTenant(),
        ...captcha.getLatestTokenHeaders()
      }
    });

    client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 405) {
          console.log("Got 405, retrying with new token");
          return new Promise((resolve, reject) => {
            captcha.showCaptcha(newToken => {
              client.defaults.headers["x-aws-waf-token"] = newToken;
              client.request(error.config).then(resolve).catch(reject);
            });
          });
        }

        return Promise.reject(error);
      }
    );

    return client;
  }, [auth0, captcha]);

  useEffect(() => {
    (async () => {
      setStatusLoading(true);
      setStatusError(null);

      try {
        const axiosClient = await getAxiosClient();
        const response = await axiosClient.get("/status");

        setStatus(response.data);

        if (response.data.status === "NO_TOKEN") {
          // No token sent yet -- request one automatically
          await axiosClient.post("/request");
        }
      } catch (e) {
        console.error("Load status error", e);
        setStatusError(
          "There was an error getting your email verification status. Please try again."
        );
      }

      setStatusLoading(false);
    })().catch(console.error);
  }, [getAxiosClient]);

  if (statusLoading) {
    return <LoadingIndicator />;
  }

  if (statusError) {
    return (
      <Alert severity="error">
        <AlertTitle>Error</AlertTitle>
        {statusError}
      </Alert>
    );
  }

  const handleRequestNewCode = async () => {
    setRequestNewLoading(true);
    setRequestNewError(null);
    setRequestNewSucceeded(false);

    try {
      const axiosClient = await getAxiosClient();
      await axiosClient.post("/request");

      setRequestNewLoading(false);
      setRequestNewSucceeded(true);
    } catch (e) {
      setRequestNewLoading(false);
      setRequestNewSucceeded(false);

      if (axios.isAxiosError(e) && e.response?.status === 400) {
        console.log("Request new error", e.response.data);
        setRequestNewError(e.response.data.errorCode);
      } else {
        setRequestNewError("UNKNOWN");
      }
    }
  };

  const handleVerify = async (evt: FormEvent) => {
    evt.preventDefault();

    if (verifyLoading || verifyCode.length !== 6) {
      return;
    }

    setVerifyLoading(true);
    setVerifyError(null);

    try {
      const axiosClient = await getAxiosClient();
      await axiosClient.post("/verify", {
        token: verifyCode
      });

      await refetchLoginInfo();
      setVerifyLoading(false);
    } catch (e) {
      console.log("Verify error", e);

      setVerifyLoading(false);

      // reset state of request-new UI so the user can request a new code if
      // needed
      setRequestNewSucceeded(false);

      if (axios.isAxiosError(e) && e.response?.status === 400) {
        setVerifyError(e.response.data.errorCode);
      } else {
        setVerifyError("UNKNOWN");
      }

      inputRef.current?.select();
    }
  };

  return (
    <div>
      <div className={classes.section}>
        <p>
          You need to verify your email address,{" "}
          <strong>{status?.email}</strong>. Please enter the code we've sent to
          your email.
        </p>
        <div className={classes.errorMessageSection}>
          {verifyError && (
            <Alert severity="error">{getVerifyErrorMessage(verifyError)}</Alert>
          )}
        </div>
        <form className={classes.inlineForm} onSubmit={handleVerify}>
          <TextField
            label="Verification Code"
            variant="outlined"
            onFocus={evt => evt.target.select()}
            value={verifyCode}
            onChange={evt => {
              setVerifyCode(cleanCodeInput(evt.target.value));
              setVerifyError(null);
            }}
            error={!!verifyError}
            className={classes.verifyInput}
            autoFocus
            inputRef={inputRef}
          />
          <SubmitButton
            disabled={verifyCode.length !== 6}
            isSubmitting={verifyLoading}
            submitText="Verify"
            className={classes.verifyButton}
          />
        </form>
      </div>

      <div className={classes.section}>
        <div>
          <RequestNew
            requestNewLoading={requestNewLoading}
            requestNewError={requestNewError}
            requestNewSucceeded={requestNewSucceeded}
            handleRequestNewCode={handleRequestNewCode}
            handleDismissDialog={() => setRequestNewError(null)}
          />
        </div>
        <LogOutLink />
      </div>
    </div>
  );
}

export default (): JSX.Element => (
  <Layout title="Email Verification Required">
    <EmailVerification />
  </Layout>
);
